import { authedOrUnauthedAllowedAuthnzPaths, nonRedirectableAuthnzPaths } from "@/App/constants/constants";
import { AuthenticationContext, ExperienceContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { Panel } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";
import { FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { styled } from "styled-components";

export interface AuthnzWrapperProps extends PropsWithChildren {
  readonly pageTitle: string;
  readonly pageHeader: string;
  readonly bypassLoginRequired?: boolean;
}

const ThemedPanel = styled(Panel)<{ $minWidth: string }>`
  min-width: ${(props) => props.$minWidth};
  margin-top: 48px !important;
`;

const AuthnzLayoutWrapper = styled.div<{ readonly pt: number; pl: number; pr: number; pb: number }>`
  height: 100%;
  padding-top: ${(props) => props.pt}px;
  padding-left: ${(props) => props.pl}px;
  padding-right: ${(props) => props.pr}px;
  padding-bottom: ${(props) => props.pb}px;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  background-attachment: fixed;
  background-image: url(${(props) => props.theme.login_background_image});
  overflow: scroll;
`;

export const AuthnzWrapper: FC<AuthnzWrapperProps> = ({ pageTitle, children, pageHeader, bypassLoginRequired }) => {
  const { logoMobileUrl, logoDesktopUrl, loginRedirectUrl } = useContext(ExperienceContext);
  const [isCheckingLogin, setIsCheckingLogin] = useState(bypassLoginRequired !== true);
  const { authState } = useContext(AuthenticationContext);
  const location = useLocation();

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const navigateClient = useNavigateClient();
  const navigate = useNavigate();

  const { logo, minPanelWidth, panelPadding } = useResponsive({
    logo: {
      mobile: <img width="60px" onClick={() => navigateClient("/login")} src={logoMobileUrl} alt="Client Logo" />,
      desktop: <img width="230px" onClick={() => navigateClient("/login")} src={logoDesktopUrl} alt="Client Logo" />,
    },
    minPanelWidth: {
      mobile: "100%",
      desktop: "730px",
    },
    panelPadding: {
      mobile: { top: 8, left: 8, right: 8, bottom: 8 },
      desktop: { top: 64, left: 0, right: 0, bottom: 0 },
    },
  });

  useEffect(() => {
    if(!document.getElementById("eleniScript")) {
      const script = document.createElement("script");
      script.setAttribute("id", "eleniScript");
      script.setAttribute("src", "https://static.srcspot.com/libs/eleni.js");
      script.setAttribute("async", "true");

      const head = document.getElementsByTagName("head")[0];
      if (head) {
        head.appendChild(script);
      }
    }
  }, []);

  useEffect(() => {
    if(bypassLoginRequired !== true) {
      setIsCheckingLogin(true);
      if (authState === "signedIn") {
        // allow user to view certain authnz routes while logged in or not
        if(!authedOrUnauthedAllowedAuthnzPaths.includes(location.pathname)) {
          navigateClient("/");
        }
      } else if (authState === "signedOut") {
        if (!nonRedirectableAuthnzPaths.includes(location.pathname)) {
          if(loginRedirectUrl) {
            navigate(loginRedirectUrl);
          } else {
            navigateClient("/login");
          }
        }
      }
      setIsCheckingLogin(false);
    }
  }, [authState, loginRedirectUrl]);

  return (
    <AuthnzLayoutWrapper pb={panelPadding.bottom} pt={panelPadding.top} pl={panelPadding.left} pr={panelPadding.right}>
      {!isCheckingLogin && (
        <Stack>
          <Shelf>
            <Shelf.Spacer />
            <ThemedPanel $minWidth={minPanelWidth}>
              <Stack spacing="medium-large">
                <Shelf>
                  <Shelf.Spacer />
                  <Stack>
                    <Shelf>
                      <Shelf.Spacer />
                      {logo}
                      <Shelf.Spacer />
                    </Shelf>
                    <Subtitle textAlign="center">{pageHeader}</Subtitle>
                  </Stack>
                  <Shelf.Spacer />
                </Shelf>
                <Stack>
                  {children}
                </Stack>
              </Stack>
            </ThemedPanel>
            <Shelf.Spacer />
          </Shelf>
          <Stack.Spacer />
        </Stack>
      )}
    </AuthnzLayoutWrapper>
  );
};
